/**
 * A picture of what the user is looking at, for a support message.
 *
 * Drawn in the browser from the page itself (html-to-image renders the DOM
 * into a canvas) — no screen-sharing prompt, no extension, nothing leaves
 * the browser until the user presses send. The library is loaded only when a
 * screenshot is taken, so it costs the rest of the app nothing.
 *
 * The help panel itself is left out of the picture (`.help-dock`), and so is
 * anything marked `data-screenshot-hide` — support wants the screen behind it.
 */

/** Widest edge of the picture; a 4K monitor does not need a 4K screenshot. */
const MAX_EDGE = 1920;

/** Scrollbars are the browser's, not the page's: left out of the picture. */
const PICTURE_CSS = '*{scrollbar-width:none!important}';

/** How long the app's fonts may take to fetch before the picture goes without them. */
const FONT_WAIT_MS = 6000;

// The app's own fonts as embeddable CSS, fetched once per page load. Without
// them the picture falls back to a system font, wider than the app's: the
// copied boxes keep their on-screen heights, so wrapped Arabic text overlaps.
// null: they could not be fetched — the picture is still taken, without them.
let fontCSS;

async function appFontCSS(getFontEmbedCSS, node) {
  if (fontCSS !== undefined) return fontCSS;
  let timer;
  try {
    fontCSS = await Promise.race([
      getFontEmbedCSS(node, { preferredFontFormat: 'woff2' }),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('fonts timed out')), FONT_WAIT_MS);
      }),
    ]);
  } catch {
    fontCSS = null;
  } finally {
    clearTimeout(timer);
  }
  return fontCSS;
}

export async function captureScreen() {
  const { toJpeg, getFontEmbedCSS } = await import('html-to-image');
  const node = document.body;
  const fonts = await appFontCSS(getFontEmbedCSS, node);
  const width = window.innerWidth;
  const height = window.innerHeight;
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const background = getComputedStyle(document.body).backgroundColor || '#0b1120';

  return toJpeg(node, {
    quality: 0.85,
    pixelRatio: scale,
    width,
    height,
    canvasWidth: Math.round(width * scale),
    canvasHeight: Math.round(height * scale),
    backgroundColor: background,
    cacheBust: false,
    // The app's fonts, fetched above with a time limit (or none: a system
    // font), never left to the library, where one font that fails to load
    // fails the whole picture.
    fontEmbedCSS: `${fonts || ''}\n${PICTURE_CSS}`,
    style: {
      // The part of the page in view, not its top.
      transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)`,
      transformOrigin: 'top left',
    },
    filter: (el) =>
      !(el && el.classList && (el.classList.contains('help-dock') || el.classList.contains('help-launcher'))) &&
      !(el && el.dataset && el.dataset.screenshotHide !== undefined),
  });
}

/** Read a picked image file as a data URL (PNG, JPEG or WebP, up to 8 MB). */
export function readImageFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('لم يتم اختيار صورة'));
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return reject(new Error('اختر صورة PNG أو JPEG أو WebP'));
    if (file.size > 8 * 1024 * 1024) return reject(new Error('الصورة أكبر من 8 ميجابايت'));
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('تعذر قراءة الصورة'));
    reader.readAsDataURL(file);
    return undefined;
  });
}
